import { useState, useRef, useCallback, useMemo } from 'react';
import JSZip from 'jszip';
import { useScore } from '../../../context/ScoreContext';
import { useMemoryWall } from '../../../hooks/useMemoryWall';

interface Props { onClose: () => void; isJudge?: boolean; }
type UploadStage = 'idle' | 'preview' | 'uploading' | 'success' | 'error';

const FONT_DECO = "'Cinzel', serif";
const FONT_HUD  = "'Orbitron', monospace";

function timeAgo(dateStr: string): string {
  const diff = Date.now() - new Date(dateStr).getTime();
  const m = Math.floor(diff / 60000);
  if (m < 1) return '剛剛';
  if (m < 60) return `${m} 分前`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h} 時前`;
  return `${Math.floor(h / 24)} 天前`;
}

const getTeamColor = (t: string) => t === 'black' ? '#d4a843' : '#c8c8c8';
const getTeamLabel = (t: string) => t === 'black' ? '⬛ 黑隊' : '⬜ 白隊';

/* ── Like burst particles — amber / orange palette ── */
const LIKE_COLORS = ['#e8900a','#f0b830','#f8d050','#d07010','#f0a020','#e06010'];
function genLikeParticles() {
  return Array.from({ length: 12 }, (_, i) => {
    const angle = (i / 12) * Math.PI * 2 + Math.random() * 0.4;
    const dist  = 20 + Math.random() * 28;
    return { id: i, dx: Math.cos(angle) * dist, dy: Math.sin(angle) * dist,
      color: LIKE_COLORS[i % LIKE_COLORS.length], size: 3 + Math.random() * 3.5, delay: Math.random() * 0.08 };
  });
}

/* ── Backdrop particles — amber / gold / orange ── */
const BG_PARTICLES = Array.from({ length: 38 }, (_, i) => ({
  id: i,
  left: `${(i * 37 + 5) % 96}%`,
  size: 1.5 + (i % 4) * 0.8,
  delay: `${(i * 0.28) % 5}s`,
  dur: `${4.5 + (i % 5) * 1.4}s`,
  drift: `${((i % 7) - 3) * 28}px`,
  color: i % 6 === 0 ? '#d0850060'
       : i % 6 === 1 ? '#c0700050'
       : i % 6 === 2 ? '#e8a83045'
       : i % 6 === 3 ? '#b0600038'
       : i % 6 === 4 ? '#d09020 30'
       : '#e0b02028',
}));

/* ── Fixed glitter stars ── */
const GLITTER = Array.from({ length: 18 }, (_, i) => ({
  id: i,
  left: `${(i * 53 + 8) % 94}%`,
  top:  `${(i * 41 + 12) % 85}%`,
  size: 2 + (i % 3) * 1.5,
  delay: `${(i * 0.6) % 4}s`,
  dur:   `${2.5 + (i % 4) * 0.8}s`,
}));

export default function MemoryWallModal({ onClose, isJudge = false }: Props) {
  const { currentMember, members } = useScore();
  const { photos, loading, uploadPhoto, toggleLike, deletePhoto } = useMemoryWall();

  const [stage, setStage]           = useState<UploadStage>('idle');
  const [previewUrl, setPreviewUrl] = useState('');
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [caption, setCaption]       = useState('');
  const [dragOver, setDragOver]     = useState(false);
  const [lightboxIdx, setLightboxIdx] = useState<number | null>(null);
  const [downloading, setDownloading] = useState(false);
  const [dlProgress, setDlProgress] = useState(0);
  const [singleDl, setSingleDl]     = useState(false);
  const [burstMap, setBurstMap]     = useState<Record<string, ReturnType<typeof genLikeParticles>>>({});
  const [hoveredId, setHoveredId]   = useState<string | null>(null);
  const [confirmDeleteId, setConfirmDeleteId] = useState<string | null>(null);
  const [sortBy, setSortBy]         = useState<'latest' | 'popular'>('latest');
  const fileInputRef = useRef<HTMLInputElement>(null);

  // ── Touch swipe refs ────────────────────────────────────────────────────────
  const touchStartX = useRef<number | null>(null);

  const teamColor = currentMember?.team === 'black' ? '#d4a843' : '#c8c8c8';
  const teamLabel = currentMember?.team === 'black' ? 'BLACK' : 'WHITE';
  useMemo(() => genLikeParticles(), []);

  // ── Sorted photos (memoised) ─────────────────────────────────────────────────
  const sortedPhotos = useMemo(() =>
    [...photos].sort((a, b) =>
      sortBy === 'popular'
        ? (b.likes ?? 0) - (a.likes ?? 0)
        : new Date(b.created_at).getTime() - new Date(a.created_at).getTime()
    ),
  [photos, sortBy]);

  // ── Masonry: split into two columns ─────────────────────────────────────────
  const leftCol  = sortedPhotos.filter((_, i) => i % 2 === 0);
  const rightCol = sortedPhotos.filter((_, i) => i % 2 !== 0);

  // ── Lightbox nav ─────────────────────────────────────────────────────────────
  const lightboxPhoto = lightboxIdx !== null ? sortedPhotos[lightboxIdx] : null;
  const goLightbox = useCallback((idx: number) => {
    const clamped = (idx + sortedPhotos.length) % sortedPhotos.length;
    setLightboxIdx(clamped);
  }, [sortedPhotos.length]);

  const handleTouchStart = (e: React.TouchEvent) => {
    touchStartX.current = e.touches[0].clientX;
  };
  const handleTouchEnd = (e: React.TouchEvent) => {
    if (touchStartX.current === null || lightboxIdx === null) return;
    const dx = e.changedTouches[0].clientX - touchStartX.current;
    if (Math.abs(dx) > 44) goLightbox(lightboxIdx + (dx < 0 ? 1 : -1));
    touchStartX.current = null;
  };

  const handleFile = useCallback((file: File) => {
    if (!file.type.startsWith('image/')) return;
    setPreviewUrl(URL.createObjectURL(file)); setSelectedFile(file); setCaption(''); setStage('preview');
  }, []);
  const handleFileInput = (e: React.ChangeEvent<HTMLInputElement>) => {
    const f = e.target.files?.[0]; if (f) handleFile(f); e.target.value = '';
  };
  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault(); setDragOver(false);
    const f = e.dataTransfer.files?.[0]; if (f) handleFile(f);
  };
  const handleSubmit = async () => {
    if (!selectedFile || !currentMember) return;
    setStage('uploading');
    const ok = await uploadPhoto(selectedFile, currentMember.name, currentMember.team, caption.trim());
    if (ok) { setStage('success'); setTimeout(() => { setStage('idle'); setPreviewUrl(''); setSelectedFile(null); setCaption(''); }, 1800); }
    else setStage('error');
  };
  const handleCancel = () => { setStage('idle'); setPreviewUrl(''); setSelectedFile(null); setCaption(''); };

  const handleLike = (e: React.MouseEvent, photoId: string) => {
    e.stopPropagation(); if (!currentMember) return;
    setBurstMap(prev => ({ ...prev, [photoId]: genLikeParticles() }));
    setTimeout(() => setBurstMap(prev => { const n = { ...prev }; delete n[photoId]; return n; }), 750);
    toggleLike(photoId, currentMember.name);
  };

  const handleDownloadAlbum = async () => {
    if (photos.length === 0 || downloading) return;
    setDownloading(true); setDlProgress(0);
    const zip = new JSZip(); const folder = zip.folder('回憶牆相簿');
    if (!folder) { setDownloading(false); return; }
    let done = 0;
    await Promise.all(photos.map(async (photo, idx) => {
      try {
        const res = await fetch(photo.photo_url); const blob = await res.blob();
        const ext = blob.type.split('/')[1]?.replace('jpeg','jpg') ?? 'jpg';
        folder.file(`${String(idx+1).padStart(3,'0')}_${photo.member_name.replace(/\s/g,'_')}.${ext}`, blob);
      } catch { /* skip */ } finally { done++; setDlProgress(Math.round((done/photos.length)*100)); }
    }));
    const blob = await zip.generateAsync({ type:'blob' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a'); a.href=url; a.download='回憶牆相簿.zip'; a.click();
    URL.revokeObjectURL(url); setDownloading(false); setDlProgress(0);
  };

  const handleSingleDownload = useCallback(async (url: string) => {
    if (singleDl) return; setSingleDl(true);
    try {
      const res = await fetch(url); const blob = await res.blob();
      const ext = blob.type.split('/')[1]?.replace('jpeg','jpg') ?? 'jpg';
      const obj = URL.createObjectURL(blob);
      const a = document.createElement('a'); a.href=obj; a.download=`回憶_${Date.now()}.${ext}`; a.click();
      URL.revokeObjectURL(obj);
    } catch { /* ignore */ } finally { setSingleDl(false); }
  }, [singleDl]);

  const handleDeleteConfirm = async (photo: ReturnType<typeof useMemoryWall>['photos'][0]) => {
    setConfirmDeleteId(null); await deletePhoto(photo);
  };

  return (
    <>
      <div className="fixed inset-0 z-50 flex items-start justify-center pt-3 pb-3 px-3 sm:pt-6 overflow-hidden">

        {/* ── Backdrop ── */}
        <div className="absolute inset-0"
          style={{ background: 'rgba(6,3,0,0.97)', backdropFilter: 'blur(16px)' }}
          onClick={stage === 'idle' ? onClose : undefined} />

        {/* Glitter stars */}
        <div className="absolute inset-0 pointer-events-none overflow-hidden">
          {GLITTER.map(g => (
            <div key={g.id} className="absolute rounded-full pointer-events-none"
              style={{ left: g.left, top: g.top, width: g.size, height: g.size,
                background: 'radial-gradient(circle,#fff,#e8a830)',
                boxShadow: `0 0 ${g.size*2}px #e8a830`,
                animation: `mwGlitter ${g.dur} ${g.delay} ease-in-out infinite`, opacity: 0 }} />
          ))}
        </div>

        {/* Ambient orbs */}
        <div className="absolute inset-0 pointer-events-none overflow-hidden">
          <div className="absolute rounded-full" style={{ width: 420, height: 420, top: '-120px', left: '0%',
            background: 'radial-gradient(circle,rgba(210,140,20,0.14) 0%,transparent 70%)', filter: 'blur(60px)', animation: 'mwOrb1 8s ease-in-out infinite' }} />
          <div className="absolute rounded-full" style={{ width: 320, height: 320, bottom: '0%', right: '-5%',
            background: 'radial-gradient(circle,rgba(190,100,10,0.12) 0%,transparent 70%)', filter: 'blur(55px)', animation: 'mwOrb2 12s ease-in-out infinite' }} />
          <div className="absolute rounded-full" style={{ width: 260, height: 260, top: '35%', left: '50%',
            background: 'radial-gradient(circle,rgba(230,170,20,0.09) 0%,transparent 70%)', filter: 'blur(45px)', animation: 'mwOrb3 15s ease-in-out infinite' }} />
          <div className="absolute rounded-full" style={{ width: 200, height: 200, top: '15%', right: '8%',
            background: 'radial-gradient(circle,rgba(170,110,10,0.08) 0%,transparent 70%)', filter: 'blur(40px)', animation: 'mwOrb2 10s 4s ease-in-out infinite' }} />
        </div>

        {/* Float particles */}
        {BG_PARTICLES.map(p => (
          <div key={p.id} className="absolute pointer-events-none rounded-full"
            style={{ left: p.left, bottom: 0, width: p.size, height: p.size, background: p.color,
              '--drift': p.drift, animation: `mwBgFloat ${p.dur} ${p.delay} ease-in-out infinite`, opacity: 0 } as React.CSSProperties} />
        ))}

        {/* ── Modal ── */}
        <div className="relative w-full max-w-2xl z-10 rounded-3xl overflow-hidden flex flex-col"
          style={{
            background: 'linear-gradient(160deg,#180e04 0%,#120800 55%,#1a1002 100%)',
            backgroundClip: 'padding-box',
            maxHeight: 'calc(100vh - 24px)',
            animation: 'mwModalIn 0.5s cubic-bezier(0.34,1.56,0.64,1)',
            boxShadow: '0 0 0 1px rgba(230,170,50,0.2), 0 0 80px rgba(200,130,20,0.18), 0 0 160px rgba(160,90,10,0.08), 0 60px 120px rgba(0,0,0,0.95)',
          }}>

          {/* Gradient border */}
          <div className="absolute inset-0 rounded-3xl pointer-events-none z-0"
            style={{ padding: '1.5px',
              background: 'linear-gradient(135deg,rgba(230,170,50,0.7),rgba(180,110,15,0.35),rgba(250,210,80,0.5),rgba(160,90,10,0.3))',
              WebkitMask: 'linear-gradient(#fff 0 0) content-box,linear-gradient(#fff 0 0)',
              WebkitMaskComposite: 'xor', maskComposite: 'exclude',
              animation: 'mwBorderRot 5s linear infinite' }} />

          {/* Scan line */}
          <div className="absolute top-0 left-0 right-0 h-[1px] overflow-hidden z-10 pointer-events-none">
            <div className="h-full" style={{ background: 'linear-gradient(90deg,transparent,rgba(230,170,60,0.95),rgba(255,215,80,0.85),transparent)', animation: 'mwScan 2.6s linear infinite' }} />
          </div>

          {/* Inner glow */}
          <div className="absolute inset-0 pointer-events-none z-0 rounded-3xl"
            style={{ background: 'radial-gradient(ellipse 80% 40% at 50% 0%,rgba(210,140,30,0.07),transparent)' }} />

          {/* Corner deco */}
          {[['top-0 left-0','right border-b border-r rounded-br-xl'],['top-0 right-0','left border-b border-l rounded-bl-xl'],
            ['bottom-0 left-0','right border-t border-r rounded-tr-xl'],['bottom-0 right-0','left border-t border-l rounded-tl-xl']].map(([pos,cls]) => (
            <div key={pos} className={`absolute ${pos} w-7 h-7 pointer-events-none z-10`}
              style={{ borderColor: 'rgba(230,170,50,0.5)', borderWidth: '1.5px', boxShadow: '0 0 10px rgba(210,140,30,0.35)' }}>
              <div className={`w-full h-full border-0 ${cls}`} style={{ borderColor: 'rgba(230,170,50,0.5)' }} />
            </div>
          ))}

          {/* ─── HEADER ─── */}
          <div className="relative flex items-center justify-between px-5 py-4 shrink-0 overflow-hidden"
            style={{ borderBottom: '1px solid rgba(220,160,40,0.18)',
              background: 'linear-gradient(90deg,rgba(200,130,20,0.14) 0%,rgba(160,90,10,0.07) 50%,transparent 100%)' }}>
            <div className="absolute inset-0 pointer-events-none"
              style={{ background: 'linear-gradient(90deg,transparent,rgba(230,170,50,0.09),transparent)', animation: 'mwShim 3.5s linear infinite' }} />

            <div className="flex items-center gap-3.5 relative z-10">
              <div className="relative">
                <div className="absolute inset-0 rounded-full" style={{ border: '1px solid rgba(230,170,50,0.45)', animation: 'mwRingPulse 2.4s ease-out infinite' }} />
                <div className="absolute inset-0 rounded-full" style={{ border: '1px solid rgba(230,170,50,0.22)', animation: 'mwRingPulse 2.4s 0.8s ease-out infinite' }} />
                <div className="w-11 h-11 flex items-center justify-center rounded-full relative z-10"
                  style={{ background: 'radial-gradient(circle,rgba(210,140,30,0.3),rgba(160,90,10,0.12))',
                    border: '1.5px solid rgba(240,180,60,0.6)',
                    boxShadow: '0 0 22px rgba(210,140,30,0.5),0 0 8px rgba(170,110,10,0.35)' }}>
                  <i className="ri-image-2-fill text-lg"
                    style={{ color: '#f0b830', filter: 'drop-shadow(0 0 7px rgba(240,170,30,1))' }} />
                </div>
              </div>
              <div>
                <div className="flex items-center gap-2">
                  <h3 className="font-black text-base tracking-[0.22em]"
                    style={{ fontFamily: FONT_DECO, color: '#f8d8c0', animation: 'mwTitleGlow 2.8s ease-in-out infinite' }}>
                    回憶牆
                  </h3>
                  <div className="flex gap-0.5">
                    {[0,200,400].map(d => <span key={d} className="w-1 h-1 rounded-full inline-block" style={{ background: '#e0a020', animation: `mwDotBlink 1.5s ${d}ms ease-in-out infinite` }} />)}
                  </div>
                </div>
                <div className="flex items-center gap-1.5 mt-0.5">
                  <i className="ri-sparkling-2-fill text-[10px]" style={{ color: 'rgba(240,190,60,0.7)', animation: 'mwSparkle 2s ease-in-out infinite' }} />
                  <p className="text-[10px] font-bold tracking-[0.2em]" style={{ color: 'rgba(240,190,60,0.55)', fontFamily: FONT_HUD }}>
                    {loading ? 'LOADING…' : `${photos.length} MEMORIES`}
                  </p>
                </div>
              </div>
            </div>

            <div className="flex items-center gap-2 relative z-10">
              {photos.length > 0 && (
                <button onClick={handleDownloadAlbum} disabled={downloading}
                  className="relative w-8 h-8 flex items-center justify-center rounded-xl cursor-pointer overflow-hidden transition-all duration-200 whitespace-nowrap"
                  style={{ background: downloading ? 'rgba(20,10,0,0.8)' : 'rgba(210,140,30,0.16)',
                    border: `1px solid ${downloading ? 'rgba(80,50,10,0.5)' : 'rgba(220,160,40,0.45)'}`,
                    color: downloading ? '#664400' : '#f0b830' }}
                  title="下載相簿">
                  {downloading && <div className="absolute left-0 top-0 bottom-0 rounded-xl transition-all" style={{ width:`${dlProgress}%`, background:'rgba(210,140,30,0.2)' }} />}
                  <span className="relative flex items-center justify-center">
                    {downloading
                      ? <i className="ri-loader-4-line animate-spin text-sm"/>
                      : <i className="ri-folder-zip-line text-sm"/>
                    }
                  </span>
                </button>
              )}
              <button onClick={onClose}
                className="w-8 h-8 flex items-center justify-center rounded-xl cursor-pointer transition-all duration-200"
                style={{ background:'rgba(210,140,30,0.06)', border:'1px solid rgba(220,160,40,0.15)', color:'#554400' }}
                onMouseEnter={e=>{ (e.currentTarget as HTMLElement).style.color='#f0b830'; (e.currentTarget as HTMLElement).style.background='rgba(210,140,30,0.15)'; }}
                onMouseLeave={e=>{ (e.currentTarget as HTMLElement).style.color='#554400'; (e.currentTarget as HTMLElement).style.background='rgba(210,140,30,0.06)'; }}>
                <i className="ri-close-line text-lg" />
              </button>
            </div>
          </div>

          {/* ─── BODY ─── */}
          <div className="flex-1 overflow-y-auto px-5 py-5 space-y-5"
            style={{ scrollbarWidth:'thin', scrollbarColor:'rgba(220,160,40,0.28) transparent' }}>

            {/* Upload zone */}
            {currentMember && (
              <div>
                {stage === 'idle' && (
                  <div className="relative rounded-2xl cursor-pointer overflow-hidden transition-all duration-300"
                    style={{ border:`1.5px dashed ${dragOver?'rgba(240,180,60,0.9)':'rgba(220,160,40,0.35)'}`,
                      background: dragOver ? 'rgba(200,130,20,0.09)' : 'rgba(170,100,10,0.03)',
                      boxShadow: dragOver ? '0 0 32px rgba(220,160,40,0.22),inset 0 0 24px rgba(220,160,40,0.07)' : 'none',
                      transform: dragOver ? 'scale(1.005)' : 'scale(1)' }}
                    onClick={() => fileInputRef.current?.click()}
                    onDragOver={e => { e.preventDefault(); setDragOver(true); }}
                    onDragLeave={() => setDragOver(false)}
                    onDrop={handleDrop}>
                    <div className="absolute inset-0 pointer-events-none"
                      style={{ background:'linear-gradient(105deg,transparent 40%,rgba(240,190,70,0.07) 50%,transparent 60%)', animation:'mwShim 3s linear infinite' }} />
                    {dragOver && ['top-2 left-2','top-2 right-2','bottom-2 left-2','bottom-2 right-2'].map(pos => (
                      <div key={pos} className={`absolute ${pos} w-2 h-2 rounded-full`}
                        style={{ background:'#e8a030', boxShadow:'0 0 10px #e8a030', animation:'mwDotBlink 0.5s ease-in-out infinite' }} />
                    ))}
                    <div className="flex items-center justify-center py-3 gap-4 select-none">
                      {/* Icon */}
                      <div className="relative shrink-0">
                        <div className="absolute inset-[-6px] rounded-xl pointer-events-none"
                          style={{ border:'1px solid rgba(220,160,60,0.22)', animation:'mwSpin 7s linear infinite' }} />
                        <div className="w-11 h-11 flex items-center justify-center rounded-xl"
                          style={{ background:'linear-gradient(135deg,rgba(210,140,30,0.22),rgba(160,90,10,0.12))',
                            border:'1.5px solid rgba(240,180,60,0.42)', animation:'mwUploadBob 3s ease-in-out infinite',
                            boxShadow:'0 0 16px rgba(210,140,30,0.2)' }}>
                          <i className="ri-camera-2-fill text-xl"
                            style={{ color:'#f0b830', filter:'drop-shadow(0 0 8px rgba(240,170,30,0.9))' }} />
                        </div>
                      </div>
                      {/* Text */}
                      <div>
                        <p className="font-black text-sm tracking-wider" style={{ color:'#f8e0c0' }}>點擊或拖曳上傳照片</p>
                        <p className="text-[11px] mt-0.5" style={{ color:'rgba(240,190,60,0.5)' }}>用照片記下每個閃亮瞬間 ✦</p>
                        <div className="flex items-center gap-1.5 mt-1.5">
                          <span className="text-[9px] px-2 py-0.5 rounded-full font-black tracking-wider"
                            style={{ background:`${teamColor}18`, color:teamColor, border:`1px solid ${teamColor}28` }}>
                            {teamLabel}
                          </span>
                          <span className="text-xs font-bold" style={{ color:'#3a2200' }}>{currentMember.name}</span>
                        </div>
                      </div>
                    </div>
                  </div>
                )}

                {stage === 'preview' && (
                  <div className="rounded-2xl overflow-hidden" style={{ border:'1.5px solid rgba(220,160,40,0.05)', animation:'mwCardIn 0.4s cubic-bezier(0.34,1.56,0.64,1)' }}>
                    <div className="relative" style={{ maxHeight:280 }}>
                      <img src={previewUrl} alt="preview" className="w-full object-cover object-top" style={{ maxHeight:280 }} />
                      <div className="absolute inset-0 pointer-events-none" style={{ background:'linear-gradient(to bottom,transparent 55%,rgba(16,8,0,0.96))' }} />
                      <div className="absolute bottom-3 left-3 flex items-center gap-2">
                        <span className="text-[9px] px-2 py-0.5 rounded-full font-black tracking-wider"
                          style={{ background:`${teamColor}25`, color:teamColor, border:`1px solid ${teamColor}45` }}>{teamLabel}</span>
                        <span className="text-xs font-bold text-white">{currentMember.name}</span>
                      </div>
                    </div>
                    <div className="p-4 space-y-3" style={{ background:'rgba(16,8,0,0.95)' }}>
                      <input type="text" maxLength={80} placeholder="寫下這一刻的心情… ✦（可選）"
                        value={caption} onChange={e=>setCaption(e.target.value)}
                        className="w-full px-3.5 py-2.5 rounded-xl text-sm outline-none transition-all"
                        style={{ background:'rgba(210,140,30,0.06)', border:'1px solid rgba(220,160,40,0.22)', color:'#f4e8c0', fontFamily:'inherit' }}
                        onFocus={e=>{ (e.target as HTMLElement).style.borderColor='rgba(240,180,60,0.55)'; (e.target as HTMLElement).style.boxShadow='0 0 16px rgba(210,140,30,0.12)'; }}
                        onBlur={e=>{ (e.target as HTMLElement).style.borderColor='rgba(220,160,40,0.22)'; (e.target as HTMLElement).style.boxShadow='none'; }}
                      />
                      <div className="flex gap-2.5">
                        <button onClick={handleCancel}
                          className="flex-1 py-2.5 rounded-xl text-xs font-bold cursor-pointer whitespace-nowrap transition-all"
                          style={{ background:'rgba(220,160,40,0.05)', border:'1px solid rgba(220,160,40,0.12)', color:'#554400' }}>取消</button>
                        <button onClick={handleSubmit}
                          className="flex-1 py-2.5 rounded-xl text-xs font-black cursor-pointer whitespace-nowrap relative overflow-hidden"
                          style={{ background:'linear-gradient(135deg,#c08010,#a06008)', color:'#fff8e0',
                            boxShadow:'0 0 22px rgba(200,130,20,0.6),0 4px 14px rgba(200,130,20,0.4)' }}>
                          <div className="absolute inset-0 opacity-40 pointer-events-none"
                            style={{ background:'linear-gradient(90deg,transparent,rgba(255,255,255,0.55),transparent)', animation:'mwShim 1.6s linear infinite' }} />
                          <span className="relative flex items-center justify-center gap-1.5">
                            <i className="ri-sparkling-fill text-xs"/>加入回憶牆
                          </span>
                        </button>
                      </div>
                    </div>
                  </div>
                )}

                {stage === 'uploading' && (
                  <div className="rounded-2xl flex flex-col items-center justify-center py-10 gap-4"
                    style={{ border:'1.5px solid rgba(220,160,40,0.25)', background:'rgba(210,140,30,0.03)' }}>
                    <div className="relative">
                      <div className="w-14 h-14 rounded-full" style={{ border:'2px solid rgba(220,160,40,0.18)', borderTop:'2px solid #c08010', animation:'spin 0.8s linear infinite' }} />
                      <i className="ri-image-2-line absolute inset-0 m-auto text-lg" style={{ color:'rgba(220,160,50,0.5)', display:'flex', alignItems:'center', justifyContent:'center' }} />
                    </div>
                    <p className="text-xs font-black tracking-[0.3em]" style={{ color:'rgba(220,160,50,0.35)', fontFamily:FONT_HUD }}>UPLOADING…</p>
                  </div>
                )}

                {stage === 'success' && (
                  <div className="rounded-2xl flex flex-col items-center justify-center py-8 gap-3"
                    style={{ border:'1.5px solid rgba(100,220,160,0.4)', background:'rgba(60,180,110,0.07)', animation:'mwCardIn 0.4s cubic-bezier(0.34,1.56,0.64,1)' }}>
                    <div className="w-14 h-14 flex items-center justify-center rounded-full"
                      style={{ background:'rgba(80,210,140,0.18)', border:'2px solid rgba(100,220,160,0.45)', boxShadow:'0 0 22px rgba(80,210,140,0.3)' }}>
                      <i className="ri-checkbox-circle-fill text-2xl" style={{ color:'#70dda8', filter:'drop-shadow(0 0 9px rgba(80,220,140,0.85))' }} />
                    </div>
                    <p className="font-black tracking-wider text-sm" style={{ color:'#70dda8' }}>✦ 已加入回憶牆！✦</p>
                  </div>
                )}

                {stage === 'error' && (
                  <div className="rounded-2xl flex flex-col items-center justify-center py-6 gap-3"
                    style={{ border:'1.5px solid rgba(240,80,80,0.3)', background:'rgba(200,40,40,0.05)' }}>
                    <i className="ri-error-warning-line text-2xl" style={{ color:'#f06060' }} />
                    <p className="text-xs" style={{ color:'#f06060' }}>上傳失敗，請再試一次</p>
                    <button onClick={handleCancel}
                      className="px-4 py-1.5 rounded-lg text-xs font-bold cursor-pointer whitespace-nowrap"
                      style={{ background:'rgba(240,80,80,0.12)', border:'1px solid rgba(240,80,80,0.38)', color:'#f06060' }}>重新選擇</button>
                  </div>
                )}
                <input ref={fileInputRef} type="file" accept="image/*" className="hidden" onChange={handleFileInput} />
              </div>
            )}

            {!currentMember && (
              <div className="rounded-2xl flex flex-col items-center justify-center py-7 gap-2.5"
                style={{ border:'1.5px dashed rgba(180,120,20,0.3)', background:'rgba(20,10,0,0.5)' }}>
                <div className="w-12 h-12 flex items-center justify-center rounded-full"
                  style={{ background:'rgba(180,120,20,0.12)', border:'1px solid rgba(180,120,20,0.25)' }}>
                  <i className="ri-lock-2-line text-xl" style={{ color:'rgba(220,160,40,0.35)' }} />
                </div>
                <p className="text-xs font-bold" style={{ color:'#3a2200' }}>加入戰局後即可留下回憶</p>
              </div>
            )}

            {/* Photo grid */}
            {loading ? (
              <div className="flex flex-col items-center justify-center py-14 gap-4">
                <div className="relative">
                  {[0,1,2].map(i => (
                    <div key={i} className="absolute rounded-full"
                      style={{ width:40+i*20, height:40+i*20, top:`${-(i*10)}px`, left:`${-(i*10)}px`,
                        border:'1px solid rgba(220,160,40,0.22)', animation:`mwRingPulse 2s ${i*0.4}s ease-out infinite` }} />
                  ))}
                  <div className="w-10 h-10 rounded-full relative z-10 flex items-center justify-center"
                    style={{ background:'rgba(210,140,30,0.12)', border:'1.5px solid rgba(220,160,40,0.45)', boxShadow:'0 0 18px rgba(210,140,30,0.25)' }}>
                    <i className="ri-gallery-line text-base" style={{ color:'rgba(230,170,50,0.4)', animation:'mwTitleGlow 2.8s ease-in-out infinite' }} />
                  </div>
                </div>
                <p className="text-[10px] font-black tracking-[0.4em]" style={{ color:'rgba(220,160,50,0.35)', fontFamily:FONT_HUD }}>LOADING</p>
              </div>
            ) : photos.length === 0 ? (
              <div className="flex flex-col items-center justify-center py-12 gap-5" style={{ animation:'mwCardIn 0.5s ease-out' }}>
                <div className="relative">
                  <div className="w-24 h-24 flex items-center justify-center rounded-3xl"
                    style={{ background:'linear-gradient(135deg,rgba(210,140,30,0.12),rgba(160,90,10,0.06))', border:'1.5px dashed rgba(220,160,40,0.25)' }}>
                    <i className="ri-camera-line text-4xl" style={{ color:'rgba(230,170,50,0.4)', animation:'mwTitleGlow 2.8s ease-in-out infinite' }} />
                  </div>
                  {[[-14,-8],[12,-16],[18,10],[-8,18]].map(([x,y],i)=>(
                    <div key={i} className="absolute w-1.5 h-1.5 rounded-full"
                      style={{ left:`calc(50% + ${x}px)`, top:`calc(50% + ${y}px)`, background:'#e8a030',
                        boxShadow:'0 0 6px #e8a030', animation:`mwSparkle 2s ${i*0.5}s ease-in-out infinite` }} />
                  ))}
                </div>
                <div className="text-center space-y-1.5">
                  <p className="font-black tracking-widest text-sm" style={{ color:'#3a2200' }}>✦ 回憶牆還空著 ✦</p>
                  <p className="text-[11px]" style={{ color:'#261600' }}>成為第一個留下閃亮回憶的人！</p>
                </div>
              </div>
            ) : (
              <div>
                {/* Divider */}
                <div className="flex items-center gap-2.5 mb-4">
                  <div className="flex-1 h-px" style={{ background:'linear-gradient(90deg,rgba(220,160,40,0.5),transparent)' }} />
                  <i className="ri-sparkling-2-fill text-[10px]" style={{ color:'rgba(240,185,55,0.55)' }} />
                  <span className="text-[9px] font-black tracking-[0.35em] uppercase"
                    style={{ color:'rgba(240,185,55,0.5)', fontFamily:FONT_HUD }}>回憶時光軸</span>
                  <i className="ri-sparkling-2-fill text-[10px]" style={{ color:'rgba(240,185,55,0.55)' }} />
                  <div className="flex-1 h-px" style={{ background:'linear-gradient(90deg,transparent,rgba(220,160,40,0.5))' }} />
                </div>

                {/* Sort tabs */}
                <div className="flex items-center gap-1.5 mb-4 p-1 rounded-xl"
                  style={{ background:'rgba(180,120,20,0.07)', border:'1px solid rgba(200,140,30,0.16)' }}>
                  {([{key:'latest',icon:'ri-time-line',label:'最新'},{key:'popular',icon:'ri-fire-fill',label:'最熱門'}] as const).map(tab => {
                    const active = sortBy === tab.key;
                    return (
                      <button key={tab.key} onClick={() => setSortBy(tab.key)}
                        className="flex-1 flex items-center justify-center gap-1.5 py-2 rounded-lg text-xs font-black cursor-pointer whitespace-nowrap transition-all duration-200"
                        style={{
                          background: active ? (tab.key==='popular'
                            ? 'linear-gradient(135deg,rgba(220,80,20,0.28),rgba(240,120,20,0.18))'
                            : 'linear-gradient(135deg,rgba(210,150,20,0.28),rgba(170,100,10,0.15))')
                            : 'transparent',
                          border: active ? `1px solid ${tab.key==='popular'?'rgba(220,80,20,0.45)':'rgba(230,170,40,0.45)'}` : '1px solid transparent',
                          color: active ? (tab.key==='popular'?'#f08030':'#f0b830') : '#3a2200',
                          boxShadow: active ? `0 0 16px ${tab.key==='popular'?'rgba(220,80,20,0.22)':'rgba(210,150,20,0.22)'}` : 'none',
                        }}>
                        <i className={`${tab.icon} text-xs`} style={{ filter: active&&tab.key==='popular'?'drop-shadow(0 0 4px rgba(240,100,20,0.9))':'none' }} />
                        {tab.label}
                        {active && tab.key==='popular' && <span className="w-1 h-1 rounded-full animate-pulse" style={{ background:'#f08030' }} />}
                        {active && tab.key==='latest'  && <span className="w-1 h-1 rounded-full animate-pulse" style={{ background:'#f0b830' }} />}
                      </button>
                    );
                  })}
                </div>

                {/* Grid — masonry two-column flex */}
                <div className="flex gap-3 items-start">
                  {[leftCol, rightCol].map((col, ci) => (
                    <div key={ci} className="flex-1 flex flex-col gap-3">
                      {col.map((photo) => {
                        const globalIdx = sortedPhotos.indexOf(photo);
                        const tc = getTeamColor(photo.team);
                        const isLiked = currentMember ? (photo.liked_by??[]).includes(currentMember.name) : false;
                        const isBurst = !!burstMap[photo.id];
                        const isHover = hoveredId === photo.id;
                        const isOwn   = photo.member_name === currentMember?.name;

                        return (
                          <div key={photo.id}
                            className="rounded-2xl overflow-visible cursor-pointer"
                            style={{ animation:`mwCardIn 0.5s ${Math.min(globalIdx*60,400)}ms cubic-bezier(0.34,1.56,0.64,1) both` }}
                            onClick={() => { if (!confirmDeleteId) setLightboxIdx(globalIdx); }}
                            onMouseEnter={() => setHoveredId(photo.id)} onMouseLeave={() => setHoveredId(null)}>
                            <div className="rounded-2xl overflow-hidden transition-all duration-300"
                              style={{
                                border:`1px solid ${isHover?'rgba(230,170,50,0.45)':'rgba(220,160,40,0.12)'}`,
                                background:'rgba(16,8,0,0.92)',
                                transform: isHover?'translateY(-4px) scale(1.015)':'translateY(0) scale(1)',
                                boxShadow: isHover?'0 10px 36px rgba(0,0,0,0.7),0 0 22px rgba(210,140,30,0.22)':'0 2px 8px rgba(0,0,0,0.45)',
                                transition:'all 0.25s cubic-bezier(0.34,1.56,0.64,1)',
                              }}>
                              {isHover && (
                                <div className="absolute top-0 left-0 right-0 h-[1px] pointer-events-none z-10"
                                  style={{ background:'linear-gradient(90deg,transparent,rgba(250,200,60,0.7),transparent)' }} />
                              )}

                              {/* Image */}
                              <div className="relative overflow-hidden">
                                <img src={photo.photo_url} alt={photo.caption || '回憶'}
                                  className="w-full object-cover object-top block transition-transform duration-500"
                                  style={{ transform: isHover ? 'scale(1.07)' : 'scale(1)' }} loading="lazy" />
                                <div className="absolute inset-0 transition-opacity duration-300 pointer-events-none"
                                  style={{ background:'linear-gradient(to bottom,transparent 50%,rgba(12,5,0,0.88))', opacity: isHover ? 1 : 0.6 }} />
                                {isOwn && isHover && confirmDeleteId !== photo.id && (
                                  <button className="absolute top-2 right-2 w-7 h-7 flex items-center justify-center rounded-full cursor-pointer z-10"
                                    style={{ background:'rgba(0,0,0,0.65)', border:'1px solid rgba(240,80,80,0.45)', backdropFilter:'blur(6px)' }}
                                    onClick={e => { e.stopPropagation(); setConfirmDeleteId(photo.id); }}>
                                    <i className="ri-delete-bin-line text-xs" style={{ color:'#f06060' }} />
                                  </button>
                                )}
                                {confirmDeleteId === photo.id && (
                                  <div className="absolute inset-0 flex flex-col items-center justify-center gap-2 z-10"
                                    style={{ background:'rgba(0,0,0,0.85)', backdropFilter:'blur(4px)', animation:'mwFadeIn 0.15s ease-out' }}
                                    onClick={e => e.stopPropagation()}>
                                    <i className="ri-delete-bin-2-fill text-xl" style={{ color:'#f06060' }} />
                                    <p className="text-xs font-black text-white tracking-wide">確定刪除？</p>
                                    <div className="flex gap-2">
                                      <button onClick={e => { e.stopPropagation(); setConfirmDeleteId(null); }}
                                        className="px-3 py-1 rounded-lg text-[10px] font-bold cursor-pointer whitespace-nowrap"
                                        style={{ background:'rgba(255,255,255,0.09)', border:'1px solid rgba(255,255,255,0.14)', color:'#888' }}>取消</button>
                                      <button onClick={e => { e.stopPropagation(); handleDeleteConfirm(photo); }}
                                        className="px-3 py-1 rounded-lg text-[10px] font-black cursor-pointer whitespace-nowrap"
                                        style={{ background:'rgba(240,80,80,0.28)', border:'1px solid rgba(240,80,80,0.55)', color:'#f06060' }}>刪除</button>
                                    </div>
                                  </div>
                                )}
                              </div>

                              {/* Info */}
                              <div className="px-3 pt-2.5 pb-3 space-y-1.5" onClick={e => { if (confirmDeleteId) e.stopPropagation(); }}>
                                <div className="flex items-center gap-2">
                                  {(() => {
                                    const memberData = members.find(m => m.name === photo.member_name);
                                    const avatarSrc = memberData?.avatarUrl;
                                    return avatarSrc ? (
                                      <img src={avatarSrc} alt={photo.member_name}
                                        className="w-7 h-7 rounded-full object-cover object-top shrink-0"
                                        style={{ border: `1.5px solid ${tc}aa` }} />
                                    ) : (
                                      <div className="w-7 h-7 rounded-full flex items-center justify-center font-black text-[12px] shrink-0 select-none"
                                        style={{
                                          background: `linear-gradient(135deg, ${tc}30, ${tc}16)`,
                                          border: `1.5px solid ${tc}aa`,
                                          color: tc,
                                          fontFamily: "'Noto Sans TC', system-ui",
                                        }}>
                                        {photo.member_name.charAt(0)}
                                      </div>
                                    );
                                  })()}
                                  <span className="text-[11px] font-black truncate" style={{ color:'#f0b830' }}>{photo.member_name}</span>
                                  <span className="text-[8px] px-1.5 py-0.5 rounded-full font-black tracking-wider shrink-0"
                                    style={{ background:`${tc}18`, color:tc, border:`1px solid ${tc}28` }}>
                                    {getTeamLabel(photo.team)}
                                  </span>
                                </div>
                                {photo.caption && (
                                  <p className="text-[11px] leading-snug italic"
                                    style={{ color:'#664400', borderLeft:'2px solid rgba(220,160,40,0.28)', paddingLeft:6 }}>
                                    {photo.caption}
                                  </p>
                                )}
                                <div className="flex items-center justify-between pt-0.5">
                                  <p className="text-[9px]" style={{ color:'#2a1800' }}>{timeAgo(photo.created_at)}</p>
                                  <div className="relative">
                                    {isBurst && burstMap[photo.id].map(p => (
                                      <div key={p.id} className="absolute pointer-events-none rounded-full z-20"
                                        style={{ width:p.size, height:p.size, left:'50%', top:'50%', background:p.color,
                                          boxShadow:`0 0 ${p.size*2}px ${p.color}`,
                                          '--dx':`${p.dx}px`, '--dy':`${p.dy}px`,
                                          animation:`mwLikePart 0.65s ${p.delay}s cubic-bezier(0.15,0.8,0.3,1) forwards`,
                                        } as React.CSSProperties} />
                                    ))}
                                    <button onClick={e => handleLike(e, photo.id)} disabled={!currentMember}
                                      className="flex items-center gap-1 px-2 py-1 rounded-xl cursor-pointer transition-all duration-200 whitespace-nowrap"
                                      style={{
                                        background: isLiked ? 'rgba(210,140,20,0.2)' : 'rgba(220,160,40,0.06)',
                                        border: `1px solid ${isLiked ? 'rgba(230,160,30,0.45)' : 'rgba(220,160,40,0.12)'}`,
                                        boxShadow: isLiked ? '0 0 14px rgba(210,140,20,0.3)' : 'none',
                                      }}>
                                      <i className={isLiked ? 'ri-heart-fill' : 'ri-heart-line'}
                                        style={{ fontSize:13, color:isLiked ? '#e09020' : '#664400',
                                          filter: isLiked ? 'drop-shadow(0 0 5px rgba(220,150,20,0.9))' : 'none', display:'block',
                                          animation: isBurst ? 'mwHeartPop 0.4s cubic-bezier(0.34,1.56,0.64,1)' : 'none' }} />
                                      {(photo.likes??0)>0 && (
                                        <span className="text-[10px] font-black" style={{ color:isLiked ? '#e09020' : '#664400' }}>
                                          {photo.likes}
                                        </span>
                                      )}
                                    </button>
                                  </div>
                                </div>
                              </div>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* ══ LIGHTBOX ══ */}
      {lightboxPhoto && (
        <div className="fixed inset-0 z-[60] flex flex-col"
          onTouchStart={handleTouchStart}
          onTouchEnd={handleTouchEnd}
          style={{ animation:'mwFadeIn 0.25s ease-out' }}>

          {/* Backdrop */}
          <div className="absolute inset-0" style={{ background:'rgba(6,3,0,0.98)', backdropFilter:'blur(22px)' }}
            onClick={() => setLightboxIdx(null)} />
          <div className="absolute inset-0 pointer-events-none flex items-center justify-center">
            <div style={{ width:560, height:560,
              background:'radial-gradient(circle,rgba(210,140,30,0.09) 0%,rgba(160,90,10,0.04) 50%,transparent 70%)',
              filter:'blur(70px)' }} />
          </div>

          {/* ── Top bar: close ── */}
          <div className="relative z-10 flex items-center justify-end px-4 pt-4 pb-2 shrink-0"
            onClick={e => e.stopPropagation()}>
            <button className="w-9 h-9 flex items-center justify-center rounded-full cursor-pointer transition-all duration-200"
              style={{ background:'rgba(210,140,30,0.08)', border:'1px solid rgba(220,160,40,0.2)', color:'#554400' }}
              onClick={() => setLightboxIdx(null)}
              onMouseEnter={e => { (e.currentTarget as HTMLElement).style.color='#f0b830'; (e.currentTarget as HTMLElement).style.background='rgba(210,140,30,0.2)'; }}
              onMouseLeave={e => { (e.currentTarget as HTMLElement).style.color='#554400'; (e.currentTarget as HTMLElement).style.background='rgba(210,140,30,0.08)'; }}>
              <i className="ri-close-line text-lg" />
            </button>
          </div>

          {/* ── Image area: flex-1, always fills remaining space ── */}
          <div className="relative z-10 flex-1 flex items-center justify-center px-4 min-h-0"
            onClick={() => setLightboxIdx(null)}>
            <img
              src={lightboxPhoto.photo_url}
              alt="回憶"
              key={lightboxPhoto.id}
              className="max-w-full max-h-full object-contain"
              style={{
                maxWidth: 'min(88vw, 700px)',
                borderRadius: 16,
                animation: 'mwLightboxIn 0.32s cubic-bezier(0.34,1.56,0.64,1)',
                boxShadow: '0 0 0 1.5px rgba(230,170,50,0.2),0 0 60px rgba(210,140,30,0.2),0 32px 80px rgba(0,0,0,0.85)',
              }}
              onClick={e => e.stopPropagation()}
            />
          </div>

          {/* ── Bottom panel: meta + controls, always fixed at bottom ── */}
          <div className="relative z-10 shrink-0 px-4 pt-3 pb-5"
            onClick={e => e.stopPropagation()}
            style={{ background: 'linear-gradient(to top, rgba(8,4,0,0.96) 0%, rgba(8,4,0,0.85) 70%, transparent 100%)' }}>

            {/* Meta row */}
            {(() => {
              const tc = getTeamColor(lightboxPhoto.team);
              const memberData = members.find(m => m.name === lightboxPhoto.member_name);
              const avatarSrc = memberData?.avatarUrl;
              return (
                <div className="flex flex-col items-center gap-1.5 mb-3"
                  style={{ animation: 'mwFadeIn 0.3s 0.1s ease-out both' }}>
                  <div className="flex items-center gap-2.5">
                    {avatarSrc ? (
                      <img src={avatarSrc} alt={lightboxPhoto.member_name}
                        className="w-7 h-7 rounded-full object-cover object-top shrink-0"
                        style={{ border: `2px solid ${tc}bb` }} />
                    ) : (
                      <div className="w-7 h-7 rounded-full flex items-center justify-center font-black text-xs shrink-0 select-none"
                        style={{ background: `linear-gradient(135deg,${tc}30,${tc}16)`, border: `2px solid ${tc}bb`, color: tc, fontFamily: "'Noto Sans TC', system-ui" }}>
                        {lightboxPhoto.member_name.charAt(0)}
                      </div>
                    )}
                    <span className="text-sm font-black" style={{ color: '#f0b830' }}>{lightboxPhoto.member_name}</span>
                    <span className="text-[9px] px-2 py-0.5 rounded-full font-black tracking-wider"
                      style={{ background: `${tc}20`, color: tc, border: `1px solid ${tc}40` }}>
                      {getTeamLabel(lightboxPhoto.team)}
                    </span>
                    <span className="text-[10px]" style={{ color: '#3a2200' }}>{timeAgo(lightboxPhoto.created_at)}</span>
                  </div>
                  {lightboxPhoto.caption && (
                    <p className="text-xs italic text-center leading-snug px-2"
                      style={{ color: 'rgba(240,190,80,0.55)', maxWidth: 280 }}>
                      ✦ {lightboxPhoto.caption}
                    </p>
                  )}
                </div>
              );
            })()}

            {/* Controls row */}
            <div className="flex items-center justify-center gap-2">
              {/* Prev */}
              {sortedPhotos.length > 1 && (
                <button
                  className="w-10 h-10 flex items-center justify-center rounded-full cursor-pointer transition-all duration-200 shrink-0"
                  style={{ background: 'rgba(210,140,30,0.12)', border: '1px solid rgba(220,160,40,0.3)', color: '#c09030' }}
                  onClick={e => { e.stopPropagation(); goLightbox(lightboxIdx! - 1); }}
                  onMouseEnter={e => { (e.currentTarget as HTMLElement).style.color='#f0b830'; (e.currentTarget as HTMLElement).style.background='rgba(210,140,30,0.24)'; }}
                  onMouseLeave={e => { (e.currentTarget as HTMLElement).style.color='#c09030'; (e.currentTarget as HTMLElement).style.background='rgba(210,140,30,0.12)'; }}>
                  <i className="ri-arrow-left-s-line text-xl" />
                </button>
              )}

              {/* Counter */}
              {sortedPhotos.length > 1 && (
                <span className="text-[11px] font-black tabular-nums px-4 py-1.5 rounded-full"
                  style={{ background: 'rgba(210,140,30,0.10)', border: '1px solid rgba(220,160,40,0.18)', color: 'rgba(240,190,60,0.6)', fontFamily: FONT_HUD }}>
                  {(lightboxIdx ?? 0) + 1} / {sortedPhotos.length}
                </span>
              )}

              {/* Download */}
              <button onClick={() => handleSingleDownload(lightboxPhoto.photo_url)} disabled={singleDl}
                className="flex items-center gap-1.5 px-4 py-2.5 rounded-2xl cursor-pointer whitespace-nowrap transition-all duration-200 shrink-0"
                style={{
                  background: singleDl ? 'rgba(16,8,0,0.9)' : 'rgba(210,140,30,0.18)',
                  border: `1px solid ${singleDl ? 'rgba(60,30,0,0.5)' : 'rgba(240,180,60,0.5)'}`,
                  color: singleDl ? '#554400' : '#f0b830',
                  backdropFilter: 'blur(12px)',
                  boxShadow: singleDl ? 'none' : '0 4px 20px rgba(210,140,30,0.25)',
                }}>
                {singleDl
                  ? <><i className="ri-loader-4-line animate-spin text-sm" /><span className="text-xs font-bold">下載中…</span></>
                  : <><i className="ri-download-2-line text-sm" /><span className="text-xs font-bold">下載</span></>
                }
              </button>

              {/* Next */}
              {sortedPhotos.length > 1 && (
                <button
                  className="w-10 h-10 flex items-center justify-center rounded-full cursor-pointer transition-all duration-200 shrink-0"
                  style={{ background: 'rgba(210,140,30,0.12)', border: '1px solid rgba(220,160,40,0.3)', color: '#c09030' }}
                  onClick={e => { e.stopPropagation(); goLightbox(lightboxIdx! + 1); }}
                  onMouseEnter={e => { (e.currentTarget as HTMLElement).style.color='#f0b830'; (e.currentTarget as HTMLElement).style.background='rgba(210,140,30,0.24)'; }}
                  onMouseLeave={e => { (e.currentTarget as HTMLElement).style.color='#c09030'; (e.currentTarget as HTMLElement).style.background='rgba(210,140,30,0.12)'; }}>
                  <i className="ri-arrow-right-s-line text-xl" />
                </button>
              )}
            </div>
          </div>
        </div>
      )}

      {/* ══ STYLES ══ */}
      <style>{`
        @keyframes mwModalIn { from{opacity:0;transform:scale(0.88) translateY(28px)} to{opacity:1;transform:scale(1) translateY(0)} }
        @keyframes mwCardIn  { from{opacity:0;transform:translateY(16px) scale(0.95)} to{opacity:1;transform:translateY(0) scale(1)} }
        @keyframes mwFadeIn  { from{opacity:0} to{opacity:1} }
        @keyframes mwLightboxIn { from{opacity:0;transform:scale(0.83)} to{opacity:1;transform:scale(1)} }
        @keyframes mwScan { from{transform:translateX(-100%)} to{transform:translateX(100%)} }
        @keyframes mwShim { from{transform:translateX(-200%)} to{transform:translateX(200%)} }
        @keyframes mwBorderRot { from{background-position:0% 50%} to{background-position:200% 50%} }
        @keyframes mwOrb1 { 0%,100%{transform:translate(0,0) scale(1)} 50%{transform:translate(28px,18px) scale(1.14)} }
        @keyframes mwOrb2 { 0%,100%{transform:translate(0,0) scale(1)} 50%{transform:translate(-22px,14px) scale(1.1)} }
        @keyframes mwOrb3 { 0%,100%{transform:translate(0,0) scale(1)} 50%{transform:translate(18px,-18px) scale(1.18)} }
        @keyframes mwBgFloat {
          0%{opacity:0;transform:translateY(0) translateX(0) scale(1)}
          12%{opacity:0.9} 85%{opacity:0.35}
          100%{opacity:0;transform:translateY(-80vh) translateX(var(--drift)) scale(0.2)}
        }
        @keyframes mwGlitter {
          0%,100%{opacity:0;transform:scale(0.3) rotate(0deg)}
          40%{opacity:1;transform:scale(1.2) rotate(180deg)}
          70%{opacity:0.6;transform:scale(0.8) rotate(270deg)}
        }
        @keyframes mwTitleGlow {
          0%,100%{text-shadow:0 0 18px rgba(230,160,30,0.7),0 0 36px rgba(180,110,10,0.3)}
          50%{text-shadow:0 0 32px rgba(255,210,50,1),0 0 64px rgba(220,160,20,0.6),0 0 90px rgba(180,120,10,0.25)}
        }
        @keyframes mwRingPulse { 0%{transform:scale(1);opacity:0.8} 100%{transform:scale(2.3);opacity:0} }
        @keyframes mwUploadBob { 0%,100%{transform:translateY(0)} 50%{transform:translateY(-7px)} }
        @keyframes mwSpin { from{transform:rotate(0deg)} to{transform:rotate(360deg)} }
        @keyframes mwDotBlink { 0%,100%{opacity:1;transform:scale(1)} 50%{opacity:0.2;transform:scale(0.6)} }
        @keyframes mwSparkle { 0%,100%{opacity:0;transform:scale(0.4) rotate(0deg)} 50%{opacity:1;transform:scale(1.4) rotate(180deg)} }
        @keyframes mwHeartPop { 0%{transform:scale(1)} 35%{transform:scale(1.8)} 65%{transform:scale(1.3)} 100%{transform:scale(1)} }
        @keyframes mwLikePart {
          0%{opacity:1;transform:translate(-50%,-50%) translate(0,0) scale(1)}
          100%{opacity:0;transform:translate(-50%,-50%) translate(var(--dx),var(--dy)) scale(0)}
        }
      `}</style>
    </>
  );
}
